import {
  isCouponReplayErrorCode,
  isPricingReplayErrorCode,
} from "./reconcile-after-sync";
import type {
  LocalCustomerDraftRecord,
  LocalCustomerDraftSyncStatus,
  LocalOrderCouponSnapshot,
  LocalOrderCustomerSnapshot,
  LocalOrderDiscountBreakdown,
  LocalOrderOperationRecord,
  LocalOrderPricingSource,
  LocalOrderRecord,
  LocalOrderVoucherSnapshot,
} from "./types";

const BLOCKING_CUSTOMER_DRAFT_STATUSES = new Set<LocalCustomerDraftSyncStatus>([
  "queued",
  "syncing",
  "failed",
  "needs_review",
]);

export function formatLocalCustomerDisplay(
  snapshot?: LocalOrderCustomerSnapshot | null,
): string | undefined {
  if (!snapshot) return undefined;
  const name = [snapshot.firstName, snapshot.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (name && snapshot.phone) return `${name} (${snapshot.phone})`;
  if (name) return name;
  if (snapshot.phone) return snapshot.phone;
  return undefined;
}

export function getCustomerSourceLabel(
  source?: LocalOrderCustomerSnapshot["source"],
): string {
  switch (source) {
    case "server":
      return "server customer";
    case "cache":
      return "cached customer";
    case "local_draft":
      return "local draft";
    default:
      return "customer";
  }
}

export function getCustomerSourceColor(
  source?: LocalOrderCustomerSnapshot["source"],
): string {
  switch (source) {
    case "server":
      return "border-green-300 bg-green-50 text-green-800";
    case "cache":
      return "border-blue-300 bg-blue-50 text-blue-800";
    case "local_draft":
      return "border-amber-300 bg-amber-50 text-amber-800";
    default:
      return "border-muted bg-muted/40 text-muted-foreground";
  }
}

export function getCustomerSyncStatusLabel(
  status?: LocalCustomerDraftSyncStatus,
): string {
  switch (status) {
    case "queued":
      return "ລໍຖ້າ sync";
    case "syncing":
      return "ກຳລັງ sync";
    case "synced":
      return "sync ແລ້ວ";
    case "failed":
      return "sync failed";
    case "needs_review":
      return "needs review";
    default:
      return status ?? "—";
  }
}

export function getCustomerSyncStatusColor(
  status?: LocalCustomerDraftSyncStatus,
): string {
  switch (status) {
    case "queued":
      return "border-amber-300 bg-amber-50 text-amber-800";
    case "syncing":
      return "border-blue-300 bg-blue-50 text-blue-800";
    case "synced":
      return "border-green-300 bg-green-50 text-green-800";
    case "failed":
    case "needs_review":
      return "border-red-300 bg-red-50 text-red-800";
    default:
      return "border-muted bg-muted/40 text-muted-foreground";
  }
}

export function getPricingSourceLabel(
  source?: LocalOrderPricingSource,
): string {
  switch (source) {
    case "network":
      return "online pricing";
    case "cache":
      return "cached pricing";
    case "manual":
      return "manual pricing";
    case "mixed":
      return "mixed pricing";
    default:
      return "—";
  }
}

export function getCouponSnapshotSourceLabel(
  source: LocalOrderCouponSnapshot["source"],
): string {
  return source === "network" ? "online" : "cache";
}

export function getVoucherSnapshotSourceLabel(
  source: LocalOrderVoucherSnapshot["source"],
): string {
  return source === "network" ? "online" : "cache";
}

function extractErrorCodeFromMessage(message?: string): string | undefined {
  if (!message) return undefined;
  for (const code of [
    "COUPON_NOT_FOUND",
    "COUPON_USAGE_LIMIT_EXCEEDED",
    "COUPON_REPLAY_FAILED",
    "CUSTOMER_SNAPSHOT_INVALID",
  ]) {
    if (message.includes(code)) return code;
  }
  return undefined;
}

export function isLocalOrderCouponConflict(order: LocalOrderRecord): boolean {
  if (order.syncStatus !== "needs_review" && order.syncStatus !== "failed") {
    return false;
  }
  const errorCode = extractErrorCodeFromMessage(order.lastSyncError);
  if (isPricingReplayErrorCode(errorCode)) return true;
  if (order.lastSyncError?.toLowerCase().includes("coupon")) return true;
  if (order.lastSyncError?.toLowerCase().includes("reward")) return true;
  if (order.lastSyncError?.includes("ຄະແນນ")) return true;
  if ((order.couponSnapshots?.length ?? 0) > 0 && order.syncStatus === "needs_review") {
    return true;
  }
  return Boolean(order.rewardSnapshot) && order.syncStatus === "needs_review";
}

export function isBlockingCustomerDraft(
  draft: LocalCustomerDraftRecord,
): boolean {
  return BLOCKING_CUSTOMER_DRAFT_STATUSES.has(draft.syncStatus);
}

const SHIFT_CLOSE_BLOCKING_SYNC_STATUSES = new Set<
  LocalOrderRecord["syncStatus"]
>(["queued", "syncing", "failed"]);

/** Unpaid OPEN orders carry over — they must not block shift close. */
export function isBlockingLocalOrderForShiftClose(
  order: LocalOrderRecord,
): boolean {
  if (order.status === "OPEN") return false;
  return SHIFT_CLOSE_BLOCKING_SYNC_STATUSES.has(order.syncStatus);
}

/** Table moves / updates on unpaid OPEN orders can wait until payment. */
export function isBlockingAdjustmentForShiftClose(
  operation: LocalOrderOperationRecord,
  order: LocalOrderRecord | undefined,
): boolean {
  if (order?.status === "OPEN") return false;
  const status = operation.syncStatus ?? "not_ready";
  return SHIFT_CLOSE_BLOCKING_SYNC_STATUSES.has(status);
}

export function isCrossShiftPaidOrderPendingSync(
  order: LocalOrderRecord,
  currentShiftId: string,
): boolean {
  const paidInShiftId = order.completedShiftId ?? order.shiftId;
  return (
    paidInShiftId === currentShiftId &&
    order.shiftId !== currentShiftId &&
    order.status !== "OPEN" &&
    isBlockingLocalOrderForShiftClose(order)
  );
}

export function formatCustomerDraftDisplay(
  draft: LocalCustomerDraftRecord,
): string {
  return (
    formatLocalCustomerDisplay(draft.customerSnapshot) ??
    draft.phone ??
    draft.localCustomerId
  );
}

export function hasDiscountBreakdown(
  breakdown?: LocalOrderDiscountBreakdown,
): breakdown is LocalOrderDiscountBreakdown {
  return Array.isArray(breakdown) && breakdown.length > 0;
}

export function isCouponInboxConflict(input: {
  errorCode?: string | null;
  errorMessage?: string | null;
  replayResult?: {
    couponReplay?: Array<{ applied: boolean; errorCode?: string }>;
  } | null;
}): boolean {
  if (isCouponReplayErrorCode(input.errorCode)) return true;
  const errorCode = extractErrorCodeFromMessage(input.errorMessage ?? undefined);
  if (isCouponReplayErrorCode(errorCode)) return true;
  return (
    input.replayResult?.couponReplay?.some(
      (entry) => !entry.applied || isCouponReplayErrorCode(entry.errorCode),
    ) ?? false
  );
}
